/**
 * DOM-based read-only "High Scores" overlay using the shared overlay palette.
 *
 * Visual structure mirrors GameOverOverlay / HelpOverlay:
 *   ┌────────────────────────────────────┐
 *   │  ★    HIGH SCORES   ★              │
 *   │           game label               │
 *   │  ┌──────────────────────────────┐  │
 *   │  │ 1.  Robert     W   1255      │  │  ← gold-tinted top row
 *   │  │ 2.  Robert     W   1230      │  │
 *   │  │ ...                          │  │
 *   │  └──────────────────────────────┘  │
 *   │           [   CLOSE   ]            │
 *   └────────────────────────────────────┘
 *
 * Usage:
 *   const lb = new LeaderboardOverlay();
 *   lb.show({ game: 'frogger', onClose: () => { ... } });
 *   lb.destroy();
 */

import { ScoreService } from '../services/ScoreService.js';
import { t } from '../config/i18n.js';
import { injectOverlayVars } from './overlayPalette.js';

let _stylesInjected = false;
function injectStyles() {
    if (_stylesInjected) return;
    _stylesInjected = true;
    injectOverlayVars();
    const css = `
    .lbov-root { position:fixed; inset:0; z-index:9100;
      display:flex; align-items:center; justify-content:center;
      background:var(--ov-backdrop);
      font-family:'Poppins','Inter','Segoe UI',Arial,sans-serif; }
    .lbov-panel { position:relative;
      background:var(--ov-panel-bg);
      border:var(--ov-panel-border-width) solid var(--ov-panel-border);
      /* Sharp corners — modal frame is square per UX direction. */
      border-radius:0;
      width:min(640px, calc(100vw - 40px));
      max-height:min(820px, calc(100vh - 40px));
      padding:30px 34px 26px;
      display:flex; flex-direction:column; gap:16px;
      box-shadow:0 24px 60px rgba(0,0,0,0.45);
      text-align:center; }
    .lbov-title-row { display:flex; align-items:center; justify-content:center; gap:22px; }
    .lbov-star { color:var(--ov-star); font-size:38px; line-height:1;
      filter:drop-shadow(0 2px 0 rgba(0,0,0,0.18)); }
    .lbov-title { font-size:40px; font-weight:900; color:var(--ov-title);
      letter-spacing:1.4px; text-shadow:0 3px 0 var(--ov-title-shadow); }
    .lbov-game { display:inline-block;
      background:var(--ov-subtitle-bg); color:var(--ov-subtitle-fg);
      font-size:14px; font-weight:800; letter-spacing:1.4px;
      padding:6px 18px; border-radius:0; align-self:center; }
    .lbov-list { display:flex; flex-direction:column; gap:8px;
      flex:1 1 auto; overflow-y:auto; padding-right:4px;
      scrollbar-width:thin; scrollbar-color:var(--ov-accent-gold) transparent; }
    .lbov-list::-webkit-scrollbar { width:8px; }
    .lbov-list::-webkit-scrollbar-thumb { background:var(--ov-accent-gold); border-radius:4px; }
    .lbov-list::-webkit-scrollbar-track { background:transparent; }
    .lbov-row { display:flex; align-items:center; gap:14px;
      background:var(--ov-row-even);
      border-radius:0; padding:12px 16px; }
    .lbov-row.is-odd { background:var(--ov-row-odd); }
    .lbov-row.is-top { background:var(--ov-top-row-bg);
      border:2px solid var(--ov-top-row-edge); padding:10px 14px; }
    .lbov-rank { width:36px; text-align:left;
      font-size:18px; font-weight:900; color:var(--ov-label);
      letter-spacing:0.5px; }
    .lbov-row.is-top .lbov-rank { color:var(--ov-accent-gold); }
    .lbov-name { flex:1 1 auto; text-align:left;
      font-size:18px; font-weight:800; color:var(--ov-value);
      overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
    .lbov-outcome { font-size:13px; font-weight:900;
      width:32px; height:24px; border-radius:0;
      display:inline-flex; align-items:center; justify-content:center;
      letter-spacing:0.4px; }
    .lbov-outcome.is-win { background:rgba(120,172,34,0.18);
      color:var(--ov-ok); border:1.5px solid rgba(120,172,34,0.55); }
    .lbov-outcome.is-loss { background:rgba(248,113,113,0.18);
      color:var(--ov-err); border:1.5px solid rgba(248,113,113,0.55); }
    .lbov-score { min-width:70px; text-align:right;
      font-size:20px; font-weight:900; color:var(--ov-accent-gold);
      font-variant-numeric:tabular-nums; }
    .lbov-empty, .lbov-err { padding:30px 12px; text-align:center;
      font-size:16px; font-weight:700; color:var(--ov-label); }
    .lbov-err { color:var(--ov-err); }
    .lbov-actions { display:flex; justify-content:center; }
    .lbov-btn { border:none; cursor:pointer; font-family:inherit;
      font-weight:900; letter-spacing:1px;
      background:var(--ov-btn-retry-bg); color:var(--ov-btn-retry-fg);
      border-bottom:5px solid var(--ov-btn-retry-edge);
      border-radius:0; padding:14px 42px;
      font-size:22px; min-width:200px;
      display:flex; align-items:center; justify-content:center; gap:10px;
      box-shadow:0 6px 0 rgba(0,0,0,0.18);
      transition:transform 0.12s ease, filter 0.12s ease; }
    .lbov-btn:hover { transform:translateY(-2px); filter:brightness(1.05); }
    .lbov-btn:active { transform:translateY(1px); box-shadow:0 3px 0 rgba(0,0,0,0.18); }
    .lbov-btn svg { width:22px; height:22px; }
    `;
    const style = document.createElement('style');
    style.dataset.lbov = '1';
    style.textContent = css;
    document.head.appendChild(style);
}

const CLOSE_ICON_SVG = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
    stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
    <path d="M6 6l12 12"/><path d="M18 6l-12 12"/></svg>`;

export class LeaderboardOverlay {
    /** @type {HTMLDivElement|null} */
    _root = null;

    /**
     * @param {{ game: string, onClose: () => void }} opts
     */
    show(opts) {
        injectStyles();
        const { game, onClose } = opts;

        const root = document.createElement('div');
        root.className = 'lbov-root';
        this._root = root;

        const closeAll = () => { this.destroy(); onClose?.(); };

        const panel = document.createElement('div');
        panel.className = 'lbov-panel';
        root.appendChild(panel);

        const titleRow = document.createElement('div');
        titleRow.className = 'lbov-title-row';
        titleRow.innerHTML = `
            <span class="lbov-star">★</span>
            <span class="lbov-title">${escapeHtml(t('score.leaderboard'))}</span>
            <span class="lbov-star">★</span>
        `;
        panel.appendChild(titleRow);

        const gameLabel = document.createElement('div');
        gameLabel.className = 'lbov-game';
        gameLabel.textContent = String(game ?? '').toUpperCase();
        panel.appendChild(gameLabel);

        const list = document.createElement('div');
        list.className = 'lbov-list';
        panel.appendChild(list);

        const loading = document.createElement('div');
        loading.className = 'lbov-empty';
        loading.textContent = '...';
        list.appendChild(loading);

        const actions = document.createElement('div');
        actions.className = 'lbov-actions';
        const closeBtn = document.createElement('button');
        closeBtn.type = 'button';
        closeBtn.className = 'lbov-btn';
        closeBtn.innerHTML = `${CLOSE_ICON_SVG}<span>${escapeHtml(t('common.close'))}</span>`;
        closeBtn.addEventListener('click', (ev) => {
            ev.preventDefault();
            ev.stopPropagation();
            closeAll();
        });
        actions.appendChild(closeBtn);
        panel.appendChild(actions);

        document.body.appendChild(root);

        this._loadScores(game, list);
    }

    async _loadScores(game, container) {
        container.innerHTML = '';
        try {
            const rows = await ScoreService.getTop(game, 15);
            if (rows.length === 0) {
                const empty = document.createElement('div');
                empty.className = 'lbov-empty';
                empty.textContent = t('score.no_scores');
                container.appendChild(empty);
                return;
            }
            rows.forEach((row, idx) => {
                const item = document.createElement('div');
                item.className = 'lbov-row' + (idx === 0 ? ' is-top' : (idx % 2 ? ' is-odd' : ''));

                const rank = document.createElement('span');
                rank.className = 'lbov-rank';
                rank.textContent = `${idx + 1}.`;
                item.appendChild(rank);

                const name = document.createElement('span');
                name.className = 'lbov-name';
                name.textContent = row.player_name;
                item.appendChild(name);

                const outcome = document.createElement('span');
                const isWin = row.outcome === 'win';
                outcome.className = 'lbov-outcome ' + (isWin ? 'is-win' : 'is-loss');
                outcome.textContent = isWin ? 'W' : 'GO';
                item.appendChild(outcome);

                const sc = document.createElement('span');
                sc.className = 'lbov-score';
                sc.textContent = String(row.score);
                item.appendChild(sc);

                container.appendChild(item);
            });
        } catch {
            const err = document.createElement('div');
            err.className = 'lbov-err';
            err.textContent = t('score.load_failed');
            container.appendChild(err);
        }
    }

    destroy() {
        if (this._root?.parentNode) {
            this._root.parentNode.removeChild(this._root);
        }
        this._root = null;
    }
}

function escapeHtml(s) {
    return String(s ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}
